// MaturaCircleSymmetryClosedTask.js
// Zadanie maturalne – zamknięte: równanie okręgu po odbiciu
// względem osi Ox, Oy lub środka układu współrzędnych.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import { CIRCLE_GRAPH_COLORS, getCircleGraphRanges } from "../../../../components/GraphPlotter/circleGraphRanges";

/* ========================================================================== */
/* 1. POMOCE                                                                   */
/* ========================================================================== */

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function formatShiftedVar(variable, shift) {
  if (shift === 0) return `${variable}^2`;
  const sign = shift > 0 ? "-" : "+";
  return `(${variable} ${sign} ${Math.abs(shift)})^2`;
}

// symetrie jak w SymmetryCircleTask
const symmetries = [
  {
    code: "OX",
    describe: () => (
      <>
        w symetrii osiowej względem osi{" "}
        <InlineMath math="Ox" /> układu współrzędnych
      </>
    ),
    reflect: ([x, y]) => [x, -y],
  },
  {
    code: "OY",
    describe: () => (
      <>
        w symetrii osiowej względem osi{" "}
        <InlineMath math="Oy" /> układu współrzędnych
      </>
    ),
    reflect: ([x, y]) => [-x, y],
  },
  {
    code: "O",
    describe: () => (
      <>
        w symetrii środkowej względem początku układu współrzędnych{" "}
        <InlineMath math="O" />
      </>
    ),
    reflect: ([x, y]) => [-x, -y],
  },
];

function equationLatex(cx, cy, r) {
  const xExpr = formatShiftedVar("x", cx);
  const yExpr = formatShiftedVar("y", cy);
  return `${xExpr} + ${yExpr} = ${r * r}`;
}

/* ========================================================================== */
/* 2. OPCJE ODPOWIEDZI                                                         */
/* ========================================================================== */

function buildOptions(a, b, r, symmetry) {
  const [ax, ay] = [a, b];
  const [cx, cy] = symmetry.reflect([ax, ay]); // poprawny środek

  const correctEq = equationLatex(cx, cy, r);

  // wszystkie cztery możliwe konfiguracje znaków środka
  const variants = [
    { name: "oryginał", center: [ax, ay] },
    { name: "OX", center: [ax, -ay] },
    { name: "OY", center: [-ax, ay] },
    { name: "O", center: [-ax, -ay] },
  ];

  const wrongEqs = [];
  for (const v of variants) {
    const [vx, vy] = v.center;
    if (vx === cx && vy === cy) continue; // to byłby poprawny
    const latex = equationLatex(vx, vy, r);
    if (!wrongEqs.includes(latex)) wrongEqs.push(latex);
    if (wrongEqs.length === 3) break;
  }

  // awaryjnie domknięcie, gdyby z jakiegoś powodu zabrakło
  while (wrongEqs.length < 3) {
    const extraShift = wrongEqs.length + 1;
    const latex = equationLatex(cx + extraShift, cy, r);
    if (latex !== correctEq && !wrongEqs.includes(latex)) {
      wrongEqs.push(latex);
    }
  }

  const all = shuffle([correctEq, ...wrongEqs]);
  const correctIndex = all.indexOf(correctEq);

  return { optionsLatex: all, correctIndex, correctCenter: { x: cx, y: cy } };
}

/* ========================================================================== */
/* 3. GENERATE TASK                                                            */
/* ========================================================================== */

function generateJune2025Original() {
  return {
    type: "MaturaCircleSymmetryClosedTask",
    data: {
      center: { a: -1, b: 2 },
      r: 3,
      symmetryCode: "OY",
      optionsLatex: [
        "(x + 1)^2 + (y + 2)^2 = 9",
        "(x + 1)^2 + (y - 2)^2 = 9",
        "(x - 1)^2 + (y + 2)^2 = 9",
        "(x - 1)^2 + (y - 2)^2 = 9",
      ],
      correctIndex: 3,
      correctCenter: { x: 1, y: 2 },
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 23,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return generateJune2025Original();
  }

  // promień 2–3, żeby ładnie mieściło się w oknie [-6, 6]
  const r = randInt(2, 3);

  // środek – obie współrzędne niezerowe, żeby odbicia dawały różne okręgi
  const pickNonZero = () => {
    const abs = randInt(1, 3); // 1..3
    return Math.random() < 0.5 ? abs : -abs;
  };

  const a = pickNonZero();
  const b = pickNonZero();

  // losujemy typ symetrii
  const symmetry = symmetries[randInt(0, symmetries.length - 1)];

  const { optionsLatex, correctIndex, correctCenter } = buildOptions(
    a,
    b,
    r,
    symmetry
  );

  return {
    type: "MaturaCircleSymmetryClosedTask",
    data: {
      center: { a, b },
      r,
      symmetryCode: symmetry.code,
      optionsLatex,
      correctIndex,
      correctCenter,
    },
  };
}

/* ========================================================================== */
/* 4. RENDER TASK                                                              */
/* ========================================================================== */

export function renderTask({ data }) {
  const { center, r, symmetryCode, optionsLatex } = data;
  const { a, b } = center;

  const xExpr = formatShiftedVar("x", a);
  const yExpr = formatShiftedVar("y", b);
  const circleEq = `${xExpr} + ${yExpr} = ${r * r}`;

  const symmetry = symmetries.find((s) => s.code === symmetryCode);

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        W kartezjańskim układzie współrzędnych{" "}
        <InlineMath math="(x,y)" /> dany jest okrąg{" "}
        <InlineMath math="O" /> o równaniu:
      </p>

      <BlockMath math={`O: ${circleEq}`} />

      <p>
        Okrąg <InlineMath math="K" /> jest obrazem okręgu{" "}
        <InlineMath math="O" />{" "}
        {symmetry ? symmetry.describe() : null}.
      </p>

      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>

      <p>
        Okrąg <InlineMath math="K" /> jest określony równaniem:
      </p>

      <div style={{ marginTop: 8 }}>
        {optionsLatex.map((latex, idx) => {
          const letter = LETTERS[idx];
          return (
            <div key={letter} style={{ marginBottom: 6 }}>
              <InlineMath math={`${letter}.\\; ${latex}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ========================================================================== */
/* 5. INPUT ABCD                                                               */
/* ========================================================================== */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const options = data.optionsLatex;
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((_, idx) => ({
    value: idx,
    label: ["A", "B", "C", "D"][idx],
    textNode: ["A", "B", "C", "D"][idx],
  }));

  const handleChange = (idx) => {
    onChange({ choiceIndex: idx });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}


/* ========================================================================== */
/* 6. CHECK ANSWER                                                             */
/* ========================================================================== */

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

/* ========================================================================== */
/* 7. RENDER USER ANSWER                                                       */
/* ========================================================================== */

export function renderUserAnswer(ans, task) {
  if (!ans || typeof ans.choiceIndex !== "number") {
    return <i>(brak odpowiedzi)</i>;
  }

  const letter = LETTERS[ans.choiceIndex] ?? "?";
  const latex = task?.data?.optionsLatex?.[ans.choiceIndex];

  return <InlineMath math={latex ? `${letter}.\\; ${latex}` : `\\text{${letter}}`} />;
}

/* ========================================================================== */
/* 8. RENDER ANSWER                                                            */
/* ========================================================================== */

export function renderAnswer({ data }) {
  const { optionsLatex, correctIndex } = data;
  const letter = LETTERS[correctIndex];
  const latex = optionsLatex[correctIndex];

  return (
    <p>
      Poprawna odpowiedź:{" "}
      <InlineMath math={`${letter}.\\; ${latex}`} />.
    </p>
  );
}

/* ========================================================================== */
/* 9. RENDER EXPLANATION                                                       */
/* ========================================================================== */

export function renderExplanation({ data }) {
  const { center, r, symmetryCode, correctCenter } = data;
  const { a, b } = center;
  const { x: ax, y: ay } = correctCenter;
  const originalCenter = { x: a, y: b };
  const { xRange, yRange, graphSize } = getCircleGraphRanges({
    circles: [
      { center: originalCenter, radius: r },
      { center: correctCenter, radius: r },
    ],
    points: [originalCenter, correctCenter],
  });

  const originalEq = equationLatex(a, b, r);
  const imageEq = equationLatex(ax, ay, r);

  const symmetryLabel =
    symmetryCode === "OX" || symmetryCode === "OY"
      ? `\\mathrm{${symmetryCode}}`
      : "O";

  const symmetrySentence = {
    OX: (
      <>
        Odbicie względem osi <InlineMath math="\text{OX}" /> zmienia znak
        współrzędnej <InlineMath math="y" />, a{" "}
        <InlineMath math="x" /> pozostaje bez zmian.
      </>
    ),
    OY: (
      <>
        Odbicie względem osi <InlineMath math="\text{OY}" /> zmienia znak
        współrzędnej <InlineMath math="x" />, a{" "}
        <InlineMath math="y" /> pozostaje bez zmian.
      </>
    ),
    O: (
      <>
        Odbicie względem <strong>środka układu</strong> zmienia znaki obu
        współrzędnych <InlineMath math="x" /> i{" "}
        <InlineMath math="y" />.
      </>
    ),
  }[symmetryCode];

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Odczytujemy środek i promień okręgu{" "}
        <InlineMath math="O" /> z równania.
      </p>
      <p>
        Równanie okręgu ma postać{" "}
        <InlineMath math={originalEq} />, więc środek ma współrzędne{" "}
        <InlineMath math={`S = (${a}, ${b})`} /> oraz promień{" "}
        <InlineMath math={`r = ${r}`} />.
      </p>

      <p>
        <strong>Krok 2.</strong> Zastanawiamy się, jak działa dana symetria na
        współrzędne środka.
      </p>
      <p>{symmetrySentence}</p>
      <p>
        Dla środka <InlineMath math={`S = (${a}, ${b})`} /> otrzymujemy obraz
        <InlineMath math={`\\;S' = (${ax}, ${ay})`} />.
      </p>

      <p>
        <strong>Krok 3.</strong> Zapisujemy równanie okręgu{" "}
        <InlineMath math="K" /> o środku <InlineMath math="S'" /> i tym samym
        promieniu <InlineMath math="r" />.
      </p>
      <p>
        Okrąg <InlineMath math="K" /> ma promień{" "}
        <InlineMath math={`r = ${r}`} />, więc jego równanie jest następujące:
      </p>

      <BlockMath math={imageEq} />

      {/* Dwa okręgi na jednym układzie, wycentrowany wykres */}
      <div
        style={{
          marginTop: "1.5rem",
          marginBottom: "1.5rem",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ width: `min(100%, ${graphSize}px)` }}>
          <GraphViewer
            points={{
              S: { ...originalCenter, color: CIRCLE_GRAPH_COLORS.point, textColor: CIRCLE_GRAPH_COLORS.point },
              "S'": { ...correctCenter, color: CIRCLE_GRAPH_COLORS.point, textColor: CIRCLE_GRAPH_COLORS.point },
            }}
            circles={[
              { center: originalCenter, radius: r, color: CIRCLE_GRAPH_COLORS.circle }, // okrąg O
              { center: correctCenter, radius: r, color: CIRCLE_GRAPH_COLORS.circle },  // okrąg K
            ]}
            xRange={xRange}
            yRange={yRange}
            width={graphSize}
            height={graphSize}
            showGrid
          />
        </div>
      </div>
    </div>
  );
}



/* ========================================================================== */
/* 10. DEFAULT EXPORT                                                          */
/* ========================================================================== */

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
